/**
 * Offline Cache — Caches locker/collection data for offline viewing.
 * Uses IndexedDB for large data (images, collectibles list) and
 * Capacitor Preferences for small settings.
 * 
 * Apple compliance: Demonstrates native offline capability beyond a web browser.
 */

const DB_NAME = 'blk_offline_cache';
const DB_VERSION = 1;
const STORES = { LOCKER: 'locker', DROPS: 'drops', SETTINGS: 'settings' };

// ============================================
// IndexedDB helpers
// ============================================

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORES.LOCKER)) {
        db.createObjectStore(STORES.LOCKER, { keyPath: 'userId' });
      }
      if (!db.objectStoreNames.contains(STORES.DROPS)) {
        db.createObjectStore(STORES.DROPS, { keyPath: 'cacheKey' });
      }
      if (!db.objectStoreNames.contains(STORES.SETTINGS)) {
        db.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const dbPut = async (storeName, data) => {
  try {
    const db = await openDB();
    const tx = db.transaction(storeName, 'readwrite');
    tx.objectStore(storeName).put(data);
    return new Promise((resolve) => { tx.oncomplete = resolve; });
  } catch { /* silent */ }
};

const dbGet = async (storeName, key) => {
  try {
    const db = await openDB();
    const tx = db.transaction(storeName, 'readonly');
    const request = tx.objectStore(storeName).get(key);
    return new Promise((resolve) => {
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => resolve(null);
    });
  } catch { return null; }
};

// ============================================
// Locker Cache
// ============================================

/**
 * Cache the user's locker collectibles for offline viewing.
 * @param {string} userId 
 * @param {Array} collectibles 
 */
export const cacheLocker = async (userId, collectibles) => {
  if (!userId || !collectibles) return;
  await dbPut(STORES.LOCKER, {
    userId,
    collectibles,
    cachedAt: Date.now(),
    count: collectibles.length,
  });
};

/**
 * Retrieve cached locker data.
 * @param {string} userId
 * @returns {{ collectibles: Array, cachedAt: number, count: number } | null}
 */
export const getCachedLocker = async (userId) => {
  if (!userId) return null;
  const data = await dbGet(STORES.LOCKER, userId);
  if (!data) return null;
  // Cache valid for 24 hours
  const age = Date.now() - data.cachedAt;
  if (age > 24 * 60 * 60 * 1000) return null;
  return data;
};

// ============================================
// Drops Cache
// ============================================

/**
 * Cache active/upcoming drops for offline display.
 */
export const cacheDrops = async (drops) => {
  await dbPut(STORES.DROPS, { cacheKey: 'active_drops', drops, cachedAt: Date.now() });
};

export const getCachedDrops = async () => {
  const data = await dbGet(STORES.DROPS, 'active_drops');
  if (!data) return null;
  const age = Date.now() - data.cachedAt;
  if (age > 60 * 60 * 1000) return null; // 1 hour
  return data.drops;
};

// ============================================
// Network Status
// ============================================

let isOnline = navigator.onLine;
const listeners = new Set();

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { isOnline = true; listeners.forEach(fn => fn(true)); });
  window.addEventListener('offline', () => { isOnline = false; listeners.forEach(fn => fn(false)); });
}

export const getNetworkStatus = () => isOnline;

export const onNetworkChange = (callback) => {
  listeners.add(callback);
  return () => listeners.delete(callback);
};

// Also try Capacitor Network plugin for more reliable detection
export const initNetworkWatcher = async () => {
  try {
    const { Network } = await import('@capacitor/network');
    const status = await Network.getStatus();
    isOnline = status.connected;
    Network.addListener('networkStatusChange', (s) => {
      isOnline = s.connected;
      listeners.forEach(fn => fn(s.connected));
    });
  } catch {
    // Web fallback already set up above
  }
};
